import { CustomLinkMarkdown } from "components/Form/components/Markdown/Markdown";
import { LazyResizeableCodeEditor } from "components/Form/lazyCodeEditorComponents";
import { MarkdownEditor } from "components/Form/components/EditableMarkdown/MarkdownEditor";
import { useState, useCallback, useRef } from "react";
import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import style from "./EditableMarkdown.module.scss";

type MarkdownViewMode = "preview" | "text";

/**
 * Renders a MARKDOWN value as interactive rendered markdown by default
 * ("preview": links are clickable and text is selectable), with editing reached
 * either through a single "edit" icon button (schema-form attributes) or, when
 * `editOnClick` is set (the record `description`), by clicking the field box
 * itself — a bordered light-grey box that reads and edits with no size/position
 * jump (ui-design-principle-edit-fields).
 *
 * A click on a link or an active text selection does NOT enter edit, so the two
 * things you normally do with rendered markdown (follow a link, select/copy text)
 * still work. The plain-text ("edit") view is a Monaco editor configured for
 * markdown (syntax highlighting), filling the full available width.
 */
export const EditableMarkdownComponent = ({
    inputProps,
    attributes,
    className,
    isReadOnly,
    isCollapsible,
    hugWhenEmpty,
    // When true (the record `description`), the field renders and edits inside one
    // bordered light-grey box and enters edit mode on click of the box — no
    // separate pencil button, no reflow. Only the record header description sets
    // this; schema-form markdown attributes keep the pencil-toggle affordance.
    // (operator-task-fe-record-description-edit-view-redesign)
    editOnClick = false,
    // When true, the edit view uses the heavier shared bordered/resizable
    // CodeEditor wrapper (line-number gutter, resize handle, fullscreen toggle)
    // that the other schema-form code fields use. This is appropriate ONLY for
    // editing a markdown ATTRIBUTE in a full open-record schema form. In compact
    // contexts — the record `description` field and markdown editing inside a
    // table/list-cell popover — that chrome is oversized and reads as an
    // out-of-place code editor (Margot, 2026-08-18), so those default to the
    // lightweight MarkdownEditor instead.
    // (operator-task-fe-markdown-codeeditor-border-and-fullscreen-toggle)
    useCodeEditor = false,
}: any) => {
    const [mode, setMode] = useState<MarkdownViewMode>("preview");
    const [isExpanded, setExpanded] = useState(false);
    const [canExpand, setCanExpand] = useState(false);
    // Pixel height to pin the edit box to, so entering edit keeps the box the same
    // size as the expanded VIEW box (Margot, 2026-09-04, problem #4). null = let the
    // editor auto-grow to its content (the non-boxed schema-form path, unchanged).
    const [editHeight, setEditHeight] = useState<number | null>(null);
    // The 65vh pixel cap the view box uses — passed to the editor as its grow
    // ceiling so the edit box grows with typing up to the same height the expanded
    // view can reach, then scrolls (Margot, 2026-09-07, #5).
    const [editMaxHeight, setEditMaxHeight] = useState<number | null>(null);
    const contentNodeRef = useRef<HTMLDivElement | null>(null);
    const showMoreNodeRef = useRef<HTMLDivElement | null>(null);
    const boxNodeRef = useRef<HTMLDivElement | null>(null);

    const markdownRef = useCallback(
        (node) => {
            contentNodeRef.current = node;
            if (node !== null) {
                if (isCollapsible) {
                    setCanExpand(node.scrollHeight - 27 > Number(style.initialHeight));
                }
            }
        },
        [isCollapsible]
    );

    // Match the edit box height to the height the expanded view box has, so clicking
    // to edit does not resize the box — even though Monaco's line-spacing differs
    // from the rendered markdown's (Margot, 2026-09-04: "the expanded view and the
    // edit view [should] have exactly the same height ... even if fewer lines are
    // shown in edit mode"). We size the editor area to the full rendered content
    // plus the show-more link (the two things the view box holds), clamped to the
    // same 65vh cap the `.boxedField` view box uses — so short and tall descriptions
    // both keep an identical box outline across view<->edit.
    const measureEditHeight = useCallback(() => {
        const content = contentNodeRef.current;
        if (!content) {
            setEditHeight(null);
            return;
        }
        const showMore = showMoreNodeRef.current;
        // +2px == .showMoreLink margin-top; the link is part of the view box height.
        const showMoreH = showMore ? showMore.offsetHeight + 2 : 0;
        let innerTarget = content.scrollHeight + showMoreH;
        let capPx: number | null = null;
        if (typeof window !== "undefined") {
            let chrome = 0;
            const box = boxNodeRef.current;
            if (box) {
                const cs = window.getComputedStyle(box);
                chrome =
                    parseFloat(cs.paddingTop) +
                    parseFloat(cs.paddingBottom) +
                    parseFloat(cs.borderTopWidth) +
                    parseFloat(cs.borderBottomWidth);
            }
            const maxInner = window.innerHeight * 0.65 - chrome; // == the view box 65vh cap
            if (maxInner > 0) {
                capPx = Math.round(maxInner);
                innerTarget = Math.min(innerTarget, maxInner);
            }
        }
        // The grow ceiling (65vh cap); the editor auto-grows to content up to here.
        setEditMaxHeight(capPx);
        // The seed height entering edit (= the expanded view box height), so the box
        // does not flash small before growing. 90 == MarkdownEditor MIN_HEIGHT.
        setEditHeight(Math.max(90, Math.round(innerTarget)));
    }, []);

    // Editing is only possible when the field is not read-only. When read-only
    // we ignore any stored mode and always render the preview.
    const isEditing = !isReadOnly && mode === "text";
    // The boxed click-to-edit affordance only applies to an editable field.
    const isBoxed = !!editOnClick && !isReadOnly;
    const isEmptyPlaceholder = !!attributes?.placeholder && !inputProps?.value;

    // Enter edit on a click of the box — but never hijack a link/button click or
    // an active text selection (both are normal things to do on rendered markdown).
    const enterEdit = useCallback((event) => {
        const target = event?.target as HTMLElement | undefined;
        if (target?.closest && target.closest("a, button")) {
            return;
        }
        const selection =
            typeof window !== "undefined" && window.getSelection ? String(window.getSelection()) : "";
        if (selection) {
            return;
        }
        measureEditHeight();
        setMode("text");
    }, [measureEditHeight]);

    // A single icon button (no label) toggles between the rendered preview and
    // the plain-text editor. Used by the non-boxed (schema-form) affordance.
    const editToggleButton = isReadOnly ? null : (
        <Button
            className={style.editToggleButton}
            variation="white"
            labelOnlyAria
            ariaLabel={isEditing ? "Preview markdown" : "Edit markdown"}
            title={isEditing ? "Preview" : "Edit"}
            icon={isEditing ? IconCollection.eyeOutline : IconCollection.pencilOutline}
            onClick={() => setMode(isEditing ? "preview" : "text")}
        />
    );

    // The plain-text editor. In the boxed variant, blurring out of the editor
    // returns to preview (click-away-to-finish), matching the field's old feel.
    const editor = useCodeEditor ? (
        <LazyResizeableCodeEditor
            input={{
                name: "editableMarkdownEditor",
                value: inputProps.value ?? "",
                onChange: (value) => inputProps.onChange?.(value),
                onFocus: () => {},
                onBlur: () => inputProps.onBlur?.(),
            }}
            fieldAttr={{ disabled: attributes?.disabled }}
            meta={{}}
            fieldProps={{
                lang: "markdown",
                label: "",
                isReadOnly: !!attributes?.disabled,
                initialHeight: 200,
                minHeight: 150,
            }}
        />
    ) : (
        <MarkdownEditor
            value={inputProps.value ?? ""}
            initialHeight={editHeight}
            maxHeight={editMaxHeight}
            // In the boxed description, keep a wheel scroll inside the editor while
            // editing rather than chaining out to the page once you reach the top/
            // bottom (Margot, 2026-09-09, #5: "in edit mode inside the description, I
            // want to scroll only there"). The schema-form path keeps the default
            // (release the wheel at the boundary).
            consumeMouseWheel={isBoxed}
            onChange={(next) => inputProps.onChange?.(next)}
            onBlur={() => {
                inputProps.onBlur?.();
                if (isBoxed) {
                    setMode("preview");
                    // Leave the description EXPANDED after editing, rather than
                    // snapping back to the collapsed clip — hiding what the user
                    // just typed reads as "everything I did disappeared" (Margot,
                    // 2026-09-02). Show-more/less can re-collapse it afterwards.
                    setExpanded(true);
                }
            }}
            autoFocus
            readOnly={attributes?.disabled}
        />
    );

    // The rendered (preview) content, shared by every non-editing render path:
    // a clip wrapper measured for the collapse threshold, a soft fade cue over
    // the clipped bottom, and a quiet "Show more/less" text link.
    const isClipCollapsed = canExpand && !isExpanded;
    // In the boxed (record-description) read view, once the description is shown in
    // full (not clipped) it must scroll inside a capped box rather than grow past
    // the viewport — see `.clipWrapScroll` (Margot, 2026-09-09, #3/#5). The
    // schema-form markdown-attribute path (non-boxed) keeps its unbounded height.
    const clipClass = [
        style.clipWrap,
        isClipCollapsed ? style.clipWrapCollapsed : "",
        isBoxed && !isClipCollapsed ? style.clipWrapScroll : "",
    ]
        .filter(Boolean)
        .join(" ");
    const collapsibleView = (
        <>
            <div className={clipClass}>
                <div ref={markdownRef} className={style.contentInner}>
                    {isEmptyPlaceholder ? (
                        <span className={style.markdownPlaceholder}>{attributes?.placeholder}</span>
                    ) : (
                        <CustomLinkMarkdown markdownString={inputProps.value ?? ""} />
                    )}
                </div>
            </div>

            {isCollapsible && canExpand && (
                <div
                    ref={showMoreNodeRef}
                    className={style.showMoreLink}
                    role="button"
                    tabIndex={0}
                    onClick={(event) => {
                        event.stopPropagation();
                        setExpanded((prev) => !prev);
                    }}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                            event.stopPropagation();
                            event.preventDefault();
                            setExpanded((prev) => !prev);
                        }
                    }}
                >
                    <Button
                        label={`Show ${isExpanded ? "less" : "more"}`}
                        icon={isExpanded ? IconCollection.chevronUp : IconCollection.chevronDown}
                        variation="clear"
                        size="small"
                    />
                </div>
            )}
        </>
    );

    // Boxed edit-on-click variant (record description): one bordered box that
    // reads and edits in place.
    if (isBoxed) {
        if (isEditing) {
            // No explicit preview/eye toggle: clicking outside the box (blur)
            // returns to the rendered view — the interaction users already know
            // from other apps, so a dedicated "view" button is redundant chrome
            // (Margot, 2026-09-04). Exit-on-blur is wired in the editor's onBlur.
            return <div className={`${style.boxedField} ${style.boxedFieldEditing}`}>{editor}</div>;
        }
        return (
            <div
                ref={boxNodeRef}
                className={`${className ?? ""} ${style.plainField}`}
                role="button"
                tabIndex={0}
                title="Click to edit"
                onClick={enterEdit}
                onKeyDown={(event) => {
                    if (event.key === "Enter") {
                        event.preventDefault();
                        measureEditHeight();
                        setMode("text");
                    }
                }}
            >
                {collapsibleView}
            </div>
        );
    }

    const content = isEditing ? editor : collapsibleView;

    // Read-only: no editor affordance — render the content exactly as before.
    if (isReadOnly) {
        return <div className={className}>{content}</div>;
    }

    // Editable (non-boxed, schema-form markdown attribute): the content fills the
    // available width, with the edit / preview icon button at the right end of the
    // row so it groups with the reset-to-default / clear-field action buttons.
    const contentClassName = `${className ?? ""} ${style.editableMarkdownContent} ${
        hugWhenEmpty && isEmptyPlaceholder ? style.editableMarkdownContentHugEmpty : ""
    }`;
    return (
        <div className={style.editableMarkdownRow}>
            <div className={contentClassName}>{content}</div>
            {editToggleButton}
        </div>
    );
};
